/** Utilitaires partagés du module logistique (A3). */
import { useQuery } from '@tanstack/react-query';
import { SelectField } from '@myron/design-system';
import { get } from '../../../shared/api/client';
import { nombre } from '../../../shared/ui/format';

export interface Entrepot { id: string; code: string; nom: string; ville: string; retraitActif: boolean; livraisonActive: boolean; statut: string; version: number; adresse: { ligne1: string; codePostal: string; ville: string; pays: string }; creneaux: { id: string; jourSemaine: number; debut: string; fin: string; capacite: number }[] }
export interface Page<T> { donnees: T[]; page: number; taille: number; total: number }

export const useEntrepots = () => useQuery({ queryKey: ['logistique', 'entrepots'], queryFn: () => get<Entrepot[]>('/admin/entrepots'), staleTime: 60_000 });

export function SelectEntrepot({ valeur, onChange, label = 'Entrepôt', tous = true }: { valeur: string; onChange: (v: string) => void; label?: string; tous?: boolean }) {
  const e = useEntrepots();
  const options = [...(tous ? [{ value: '', label: 'Tous mes entrepôts' }] : []), ...(e.data ?? []).map((x) => ({ value: x.id, label: `${x.code} — ${x.nom}` }))];
  return <SelectField label={label} value={valeur} onChange={(ev) => onChange(ev.target.value)} options={options} />;
}

/** Quantité en unités de base, avec l'équivalent palettes quand il est connu. */
export function unites(q: number | null | undefined, parPalette?: number | null) {
  if (q === null || q === undefined) return '—';
  const pal = parPalette ? ` (${(q / parPalette).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pal.)` : '';
  return `${nombre(q)} u.${pal}`;
}

export const LIBELLE_MOUVEMENT: Record<string, string> = {
  RECEPTION: 'Réception', RESERVATION: 'Réservation', LIBERATION: 'Libération', SORTIE: 'Sortie', AJUSTEMENT_POSITIF: 'Ajustement +', AJUSTEMENT_NEGATIF: 'Ajustement −',
  TRANSFERT_SORTIE: 'Transfert sortant', TRANSFERT_ENTREE: 'Transfert entrant', BLOCAGE: 'Blocage', DEBLOCAGE: 'Déblocage', RETOUR: 'Retour',
};
export const LIBELLE_IMPORT: Record<string, string> = {
  PRODUITS: 'Catalogue produits', PRIX_BASE: 'Prix de base', TARIFS_CLIENTS: 'Tarifs clients', STOCKS: 'Stocks', FOURNISSEURS: 'Fournisseurs', REFERENCES_FOURNISSEUR: 'Références et tarifs fournisseurs',
};
export const JOURS = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/** Téléchargement d'un texte (CSV) côté navigateur. */
export function telecharger(nom: string, contenu: string, type = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([contenu], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** CSV sûr : neutralise les cellules interprétables comme formules (SEC-09.3). */
export function csvSur(lignes: (string | number | null | undefined)[][]) {
  const cel = (v: string | number | null | undefined) => {
    let s = v === null || v === undefined ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?\d+([.,]\d+)?$/.test(s)) s = `'${s}`;
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return `﻿${lignes.map((l) => l.map(cel).join(';')).join('\n')}\n`;
}
